import type { ReactNode } from "react";
import { AlertTriangleIcon } from "./icons";

// Alert — DS §4.7 + preview/alert-cards.html. White surface ALWAYS; role color
// lives only in the 1px border + chip/icon. Title is Inter 600/18 (sans, NOT
// serif). Body indented 36px to align under the title.

export type AlertRole = "warning" | "danger" | "info";

export interface AlertProps {
    role: AlertRole;
    /**
     * Lay the header out as a horizontal BANNER instead of a card: one row, the
     * title and its sub running inline, tighter padding.
     *
     * Opt-in, and it has to be. The card is right where an alert is a block on a
     * page with room — a settings panel, a calendar notice — and there are ~70 of
     * those. It is wrong where the alert is one sentence at the top of a screen
     * the reader is trying to get through, which is what prompted this: the
     * signing surface spent ~110px explaining a timeout above the field it wanted
     * you to type in.
     *
     * `children` and `actions` still render beneath, so a banner that grows a body
     * degrades into something card-shaped rather than breaking.
     */
    banner?: boolean;
    /**
     * Announce this alert to a screen reader when it appears.
     *
     * OFF BY DEFAULT, and the default is the important half. Whether an alert
     * should be announced depends on whether it is an UPDATE or STANDING
     * CONTENT — NOT on its role, which is why this is a prop and not something
     * derived from `role`:
     *
     *   UPDATE — it rendered because of something the user just did. A failed
     *     save, a code that didn't verify. A sighted user sees the box arrive;
     *     without this, a screen-reader user is told nothing. WCAG 4.1.3 (AA).
     *     → pass `live`.
     *   STANDING — it is part of what this screen says, present on arrival.
     *     "This couple is archived." NoInviteEmailNotice renders on ~98.5% of
     *     couple pages: a live role there makes a screen reader re-announce
     *     unchanged content on every navigation, and the users least able to
     *     skip the repetition get the most of it. That exact regression shipped
     *     once and is now pinned by a test.
     *     → leave it off.
     *
     * A `danger` alert is USUALLY an update and a `warning` usually standing,
     * but neither is reliable enough to infer — the caller knows, the role
     * doesn't.
     */
    live?: boolean;
    title: string;
    /** One-line detail under the title — usually the error text. 50 of 52. */
    sub?: string;
    /** Body copy, indented under the title. 18 of 52. */
    children?: ReactNode;
    /** Action row beneath a top border. 14 of 52. */
    actions?: ReactNode;
}

/* THREE PROPS WERE REMOVED, all with zero callers across 52 production alerts:
   `icon` (the role already picks one), `meta` (a bordered row of icon+text
   pairs) and `proposes` (a "Dova proposes" block in success green). `proposes`
   in particular had a gallery fixture and no caller anywhere, which is how a
   dead prop kept looking load-bearing — the gallery demonstrated a shape the
   product had never asked for. If a surface needs one back, it is in git with
   its markup intact. */

const BORDER: Record<AlertRole, string> = {
    warning: "border-warning-bd",
    danger: "border-danger-bd",
    info: "border-info-bd",
};

/** The role's TINT, for the banner variant. The card stays --surface always (the
 *  DS rule at the top of this file); a strip pinned to the top edge of something
 *  has no border to carry its role, so the fill does it instead. */
const TINT: Record<AlertRole, string> = {
    warning: "bg-warning-bg",
    danger: "bg-danger-bg",
    info: "bg-info-bg",
};

const ICON_COLOR: Record<AlertRole, string> = {
    warning: "text-warning",
    danger: "text-danger",
    info: "text-info",
};

function InfoIcon() {
    return (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <circle cx="12" cy="12" r="9" />
            <line x1="12" y1="11" x2="12" y2="16" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
    );
}

/* `live` maps to the ARIA role that matches the TONE — assertive for a failure
   the user is about to act past, polite for anything else. */
const LIVE_REGION: Record<AlertRole, "alert" | "status"> = {
    danger: "alert",
    warning: "status",
    info: "status",
};

export function Alert({ role, live = false, banner = false, title, sub, actions, children }: AlertProps) {
    const defaultIcon = role === "info" ? <InfoIcon /> : <AlertTriangleIcon />;
    // The header's bottom margin is for the gap to `children` or `actions`. With
    // neither, it was 16px of nothing at the bottom of every title-and-sub alert,
    // which is most of them.
    const headerGap = children || actions ? "mb-4" : "";
    return (
        <div
            role={live ? LIVE_REGION[role] : undefined}
            className={
                banner
                    // NO radius and no outer border: a banner is meant to be bled to
                    // the edges of whatever hosts it, and both would fight that. The
                    // host owns the bleed (it is the only thing that knows its own
                    // padding) and any corner rounding it needs to match. What the
                    // strip owns is the tint, the hairline under it, and the padding
                    // inside it.
                    ? `border-b ${BORDER[role]} ${TINT[role]} px-6 py-3`
                    : `rounded-[var(--radius-lg)] border bg-surface ${BORDER[role]} px-6 py-6`
            }
        >
            <div
                className={`${headerGap} grid items-start ${
                    banner ? "grid-cols-[auto_1fr] gap-3" : "grid-cols-[auto_1fr_auto] gap-4"
                }`}
            >
                <span className={`mt-0.5 ${ICON_COLOR[role]}`}>{defaultIcon}</span>
                <div className="min-w-0">
                    {banner ? (
                        // Title and sub share one paragraph so a short alert is one
                        // line and a longer one wraps as prose, rather than always
                        // costing two stacked blocks.
                        <p className="[font:var(--type-body-sm)] text-secondary">
                            <span className="font-semibold text-primary">{title}</span>
                            {sub ? <> {sub}</> : null}
                        </p>
                    ) : (
                        <>
                            <h3 className="font-sans text-lg font-semibold leading-[1.3] tracking-[-0.005em] text-primary">
                                {title}
                            </h3>
                            {sub && (
                                <p className="mt-1 font-sans text-xs leading-[1.45] text-secondary">
                                    {sub}
                                </p>
                            )}
                        </>
                    )}
                </div>
            </div>

            {children && (
                <div className="mb-4 ml-8 font-sans text-base leading-[1.55] text-secondary">
                    {children}
                </div>
            )}



            {actions && (
                <div className="ml-8 flex items-center gap-4 border-t border-border pt-4">{actions}</div>
            )}
        </div>
    );
}
